/**
 * RaceTargetModal — modal unificado Add+Edit de race_target (v15.5-§8.3.D).
 *
 * Modos:
 * - `mode="create"`: form vacío, POST /races, título "Añadir carrera".
 * - `mode="edit"`: form pre-poblado desde `race`, PATCH /races/:id,
 *   título "Editar carrera".
 *
 * Campos: name (opcional), race_date (required, futura), distance
 * (required, allowlist), surface (required, allowlist).
 *
 * NO expone `is_primary` — el backend rechaza con
 * `cannot_change_is_primary_here` y la promoción es §8.3.E (acción
 * separada en RaceCard).
 *
 * Validación cliente fail-fast antes del API. Errores API mapeados a
 * mensajes i18n inline (no toast, no cierra modal).
 *
 * Patrón visual y de estado replicado de AddRaceModal.tsx (§8.4):
 * mismo overlay, mismo size (max-w-sm), mismos botones cancel/submit.
 */
import { useState } from "react";

import { createRace, updateRace } from "@/api/races";
import { t } from "@/i18n/es";
import { getAllowedRaceDistances } from "@/lib/raceDistance";
import { ApiError, type AthleteLevel, type RaceTarget } from "@/types/api";

/** 8 valores del enum backend ALLOWED_RACE_DISTANCES. */
const DISTANCE_OPTIONS: ReadonlyArray<{
  value: string;
  labelKey: keyof typeof t.plan;
}> = [
  { value: "5k", labelKey: "raceDistance5k" },
  { value: "10k", labelKey: "raceDistance10k" },
  { value: "15k", labelKey: "raceDistance15k" },
  { value: "20k", labelKey: "raceDistance20k" },
  { value: "half_marathon", labelKey: "raceDistanceHalfMarathon" },
  { value: "marathon", labelKey: "raceDistanceMarathon" },
  { value: "trail_short", labelKey: "raceDistanceTrailShort" },
  { value: "trail_long", labelKey: "raceDistanceTrailLong" },
];

/** 3 valores del enum backend ALLOWED_RACE_SURFACES. */
const SURFACE_OPTIONS: ReadonlyArray<{
  value: "asfalto" | "trail" | "mixto";
  labelKey: keyof typeof t.profile;
}> = [
  { value: "asfalto", labelKey: "surfaceAsfalto" },
  { value: "trail", labelKey: "surfaceTrail" },
  { value: "mixto", labelKey: "surfaceMixto" },
];

/** Bounds del date picker: mañana .. hoy+365, en ISO LOCAL. Copy de
 *  AddRaceModal §8.4 (12 líneas, no merece extraer a lib). */
function _dateBounds(): { minIso: string; maxIso: string } {
  const today = new Date();
  const min = new Date(today);
  min.setDate(today.getDate() + 1);
  const max = new Date(today);
  max.setDate(today.getDate() + 365);
  const toIso = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
      d.getDate(),
    ).padStart(2, "0")}`;
  return { minIso: toIso(min), maxIso: toIso(max) };
}

/** Mapea error codes del backend a i18n keys de `t.races.*`. */
function _errorMessage(code: string): string {
  switch (code) {
    case "race_date_required":
    case "race_date_invalid":
      return t.races.errorDateRequired;
    case "race_date_in_past_or_today":
      return t.races.errorDateInPast;
    case "race_distance_required":
      return t.races.errorDistanceRequired;
    case "invalid_distance":
      return t.races.errorInvalidDistance;
    case "surface_required":
      return t.races.errorSurfaceRequired;
    case "invalid_surface":
      return t.races.errorInvalidSurface;
    case "beginner_max_5k":
      return t.plan.errorBeginnerMax5k;
    case "max_races_reached":
      return t.races.errorMaxReached;
    case "cannot_change_is_primary_here":
      // Defensa — UI no expone el campo, pero por si acaso.
      return t.races.errorGeneric;
    case "race_target_not_found":
    case "race_target_not_owned":
      return t.races.errorNotFoundOrOwned;
    default:
      return t.races.errorGeneric;
  }
}

interface Props {
  mode: "create" | "edit";
  /** Requerido en mode="edit"; ignorado en "create". */
  race?: RaceTarget;
  /** Nivel del atleta para filtrar distance (gate §8.4). null = no filtra. */
  athleteLevel: AthleteLevel | null;
  onClose: () => void;
  /** Llamado tras 200/201. El padre debe hacer refresh() de la lista. */
  onSaved: () => void;
}

export function RaceTargetModal({
  mode, race, athleteLevel, onClose, onSaved,
}: Props) {
  const isEdit = mode === "edit";
  const [name, setName] = useState(isEdit ? (race?.name ?? "") : "");
  const [raceDate, setRaceDate] = useState(isEdit ? (race?.race_date ?? "") : "");
  const [distance, setDistance] = useState(isEdit ? (race?.distance ?? "") : "");
  const [surface, setSurface] = useState<string>(
    isEdit ? (race?.surface ?? "asfalto") : "asfalto",
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { minIso, maxIso } = _dateBounds();

  // Filtrar distance options por nivel (§8.4 reuso).
  const allowedDistanceValues = getAllowedRaceDistances(
    athleteLevel,
    DISTANCE_OPTIONS.map((o) => o.value),
  );
  const filteredDistanceOptions = DISTANCE_OPTIONS.filter((opt) =>
    allowedDistanceValues.includes(opt.value),
  );

  async function submit() {
    setSubmitting(true);
    setError(null);
    try {
      if (isEdit && race) {
        await updateRace(race.id, {
          // Solo enviamos campos potencialmente cambiados. Para name,
          // el backend interpreta null como "borrar nombre"; mantenemos
          // ese contrato.
          name: name.trim() || null,
          race_date: raceDate,
          distance,
          surface,
        });
      } else {
        await createRace({
          name: name.trim() || null,
          race_date: raceDate,
          distance,
          surface,
        });
      }
      onSaved();
      onClose();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(_errorMessage(err.code));
      } else {
        setError(t.races.errorGeneric);
      }
      setSubmitting(false);
    }
  }

  function onSubmitForm(e: React.FormEvent) {
    e.preventDefault();
    // Validación cliente fail-fast (mismo orden que backend para
    // consistencia de mensajes — fail con el mismo error que ofrecería
    // el backend).
    if (!raceDate) {
      setError(t.races.errorDateRequired);
      return;
    }
    if (!distance) {
      setError(t.races.errorDistanceRequired);
      return;
    }
    if (!surface) {
      setError(t.races.errorSurfaceRequired);
      return;
    }
    void submit();
  }

  return (
    <div
      data-testid="race-target-modal"
      data-mode={mode}
      role="dialog"
      aria-modal="true"
      aria-labelledby="race-target-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
    >
      <div className="w-full max-w-sm rounded-lg border border-border-subtle bg-carbon-raised p-5">
        <h2
          id="race-target-modal-title"
          className="text-lg font-medium text-bone"
        >
          {isEdit ? t.races.editModalTitle : t.races.addModalTitle}
        </h2>

        <form
          data-testid="race-target-form"
          onSubmit={onSubmitForm}
          className="mt-4 flex flex-col gap-4"
          noValidate
        >
          {/* Name opcional */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor="race-target-name"
              className="text-xs uppercase tracking-widest text-gray-mid"
            >
              {t.races.fieldNameLabel}
            </label>
            <input
              id="race-target-name"
              data-testid="race-target-name-input"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={200}
              placeholder={t.races.fieldNamePlaceholder}
              className="rounded-md border border-border-subtle bg-graphite px-3 py-2 text-sm text-bone focus:border-border-strong focus:outline-none"
            />
          </div>

          {/* Fecha */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor="race-target-date"
              className="text-xs uppercase tracking-widest text-gray-mid"
            >
              {t.races.fieldDateLabel}
            </label>
            <input
              id="race-target-date"
              data-testid="race-target-date-input"
              type="date"
              value={raceDate}
              min={minIso}
              max={maxIso}
              onChange={(e) => setRaceDate(e.target.value)}
              required
              className="rounded-md border border-border-subtle bg-graphite px-3 py-2 text-sm text-bone focus:border-border-strong focus:outline-none"
            />
          </div>

          {/* Distancia (filtrada por nivel) */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor="race-target-distance"
              className="text-xs uppercase tracking-widest text-gray-mid"
            >
              {t.races.fieldDistanceLabel}
            </label>
            <select
              id="race-target-distance"
              data-testid="race-target-distance-select"
              value={distance}
              onChange={(e) => setDistance(e.target.value)}
              required
              className="rounded-md border border-border-subtle bg-graphite px-3 py-2 text-sm text-bone focus:border-border-strong focus:outline-none"
            >
              <option value="">—</option>
              {filteredDistanceOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {t.plan[opt.labelKey]}
                </option>
              ))}
            </select>
          </div>

          {/* Superficie */}
          <fieldset className="flex flex-col gap-2">
            <legend className="text-xs uppercase tracking-widest text-gray-mid">
              {t.races.fieldSurfaceLabel}
            </legend>
            <div className="flex flex-wrap gap-3">
              {SURFACE_OPTIONS.map((opt) => (
                <label
                  key={opt.value}
                  className="flex cursor-pointer items-center gap-2 text-sm text-bone"
                >
                  <input
                    type="radio"
                    name="race-target-surface"
                    value={opt.value}
                    data-testid={`race-target-surface-${opt.value}`}
                    checked={surface === opt.value}
                    onChange={() => setSurface(opt.value)}
                  />
                  {t.profile[opt.labelKey]}
                </label>
              ))}
            </div>
          </fieldset>

          {error && (
            <p
              data-testid="race-target-error"
              role="alert"
              className="text-sm text-danger"
            >
              {error}
            </p>
          )}

          <div className="mt-1 flex items-center justify-end gap-3">
            <button
              type="button"
              data-testid="race-target-cancel"
              onClick={onClose}
              disabled={submitting}
              className="rounded-md border border-border-subtle px-4 py-2 text-sm text-bone-muted hover:border-border-strong hover:text-bone disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t.races.modalCancel}
            </button>
            <button
              type="submit"
              data-testid="race-target-submit"
              disabled={submitting}
              className="rounded-md bg-lime px-4 py-2 text-sm font-medium text-graphite hover:bg-lime/90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isEdit ? t.races.modalSubmitEdit : t.races.modalSubmitCreate}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
